import { create } from 'zustand';
import axios from 'axios';
import { API } from '../utils/api';

const useAuthStore = create((set, get) => ({
  user: null,
  isLoading: true,
  isAuthenticated: false,

  setUser: (user) => set({ user, isAuthenticated: !!user }),

  login: async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    set({ user: response.data.user, isAuthenticated: true });
  },

  register: async (email, password, name, phone, referralCode, nif, companyName, accountType, requestedQrQuota) => {
    const response = await axios.post(`${API}/auth/register`, { 
      email, password, name, phone, referral_code: referralCode,
      nif: nif || null, company_name: companyName || null,
      account_type: accountType || 'agent',
      requested_qr_quota: requestedQrQuota || null
    });
    
    if (response.data.email_verification_required) {
      return { email_verification_required: true };
    }
    
    if (response.data.user) {
      set({ user: response.data.user, isAuthenticated: true });
    }
    
    return response.data;
  },

  logout: async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) {
      // Logout should succeed even if server call fails — log for diagnostics only.
      if (process.env.NODE_ENV !== 'production') {
        console.debug('[auth] logout server call failed (non-fatal):', e?.message);
      }
    }
    // Clean up any remaining localStorage items from old auth
    localStorage.removeItem('token');
    set({ user: null, isAuthenticated: false });
  },

  checkAuth: async () => {
    set({ isLoading: true });
    try {
      const response = await axios.get(`${API}/auth/me`);
      set({ user: response.data, isAuthenticated: true, isLoading: false });
    } catch (e) {
      // Clean up old localStorage token if present
      localStorage.removeItem('token');
      set({ user: null, isAuthenticated: false, isLoading: false });
    }
  },

  fetchUser: async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      set({ user: response.data });
    } catch (e) {
      console.error('Error fetching user:', e);
    }
  },

  // Called by the Google OAuth callback page after a successful backend exchange.
  // The cookie is already set server-side; we just need to populate the store.
  setAuthFromGoogle: async ({ user }) => {
    if (user) {
      set({ user, isAuthenticated: true, isLoading: false });
    } else {
      // Defensive: refresh from /auth/me if the response didn't include the user
      try {
        const response = await axios.get(`${API}/auth/me`);
        set({ user: response.data, isAuthenticated: true, isLoading: false });
      } catch (e) {
        set({ user: null, isAuthenticated: false, isLoading: false });
      }
    }
  },

  refreshUser: async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      set({ user: response.data, isAuthenticated: true });
    } catch (e) {
      // Stay silent — caller will handle navigation if needed. Log in dev only.
      if (process.env.NODE_ENV !== 'production') {
        console.debug('[auth] refreshUser failed:', e?.response?.status, e?.message);
      }
    }
  }
}));

export default useAuthStore;
